import React, {
  Suspense,
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

// Pages - using absolute imports with baseUrl
import HomePage from "pages/HomePage";
import NotFoundPage from "pages/NotFoundPage";
import LoginPage from "pages/auth/components/LoginPage";
import UserProfilePage from "pages/auth/components/UserProfilePage";
import AccountsPage from "pages/accounts/AccountsPage";
import EditAccountPage from "pages/accounts/EditAccountPage";
import InactivityCenterPage from "pages/accounts/PreviewDeactivatePage";
import UsersPage from "pages/accounts/users/UsersPage";
import EditUserPage from "pages/accounts/users/EditUserPage";
import DashboardsPage from "pages/dashboards/DashboardsPage";
import UserSolutionsPage from "pages/accounts/users/UserSolutionsPage";
import UserSubscriptionsPage from "pages/accounts/users/UserSubscriptionsPage";
import AlertsPage from "pages/alerts/AlertsPage";
import HXCPartnerCreationPage from "pages/HXCPartnerCreation/HXCPartnerCreationPage";
import { sections } from "features/data-management/config/sections";
import SectionErrorBoundary from "features/data-management/components/SectionErrorBoundary";
import DataManagementRouteGuard from "features/data-management/DataManagementRouteGuard";
import RegionSyncPage from "pages/region-sync/RegionSyncPage";
import ApiKeysPage from "pages/accounts/api-keys/ApiKeysPage";
import ParticipantsPage from "pages/participants/ParticipantsPage";

// Components
import TopBar from "components/TopBar";
import LeftNav from "components/LeftNav";
import ProtectedRoute from "components/ProtectedRoute";
import LoadingBar from "components/LoadingBar";
import RouteGuard from "components/RouteGuard";
import DefaultRedirect from "components/DefaultRedirect";

// Contexts and Providers
import { AuthProvider } from "contexts/AuthContext";
import Auth0ProviderWithNavigate from "contexts/Auth0ProviderWithNavigate";
import { ThemeProvider, useTheme } from "contexts/ThemeContext";

// Services
import { setLoadingController } from "services/ApiService";
import { createAppTheme } from "services/ThemeService";

// Create a loading context
interface LoadingContextType {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

const LoadingContext = createContext<LoadingContextType>({
  isLoading: false,
  setIsLoading: () => {},
});

export const useLoading = () => useContext(LoadingContext);

const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "50vh",
    }}
  >
    <CircularProgress />
  </Box>
);

/**
 * App Layout component that provides the main UI structure
 */
const AppLayout = () => {
  const { isLoading } = useLoading();
  const { isDarkMode } = useTheme();

  return (
    <Box sx={{ display: "flex" }}>
      <LoadingBar isLoading={isLoading} />
      <LeftNav />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: "60px",
          minHeight: "100vh",
          backgroundColor: isDarkMode ? "#1e1e1e" : "#f5f5f5",
        }}
      >
        <TopBar />
        <Container sx={{ mt: 7, mb: 4, py: 2 }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

/**
 * Main App component that manages application state
 */
function App() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { mode } = useTheme();

  // Connect the loading controller to the API service
  useEffect(() => {
    setLoadingController({ setIsLoading });
  }, []);

  // Create the theme based on current mode using the ThemeService
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <LoadingContext.Provider value={{ isLoading, setIsLoading }}>
        <Router>
          {/* Auth0Provider needs to be inside Router to have access to navigation */}
          <Auth0ProviderWithNavigate>
            {/* Original AuthProvider now wraps inside Auth0Provider */}
            <AuthProvider>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />

                {/* Development testing routes - COMMENTED OUT to fix "Open in New Tab" issue.
                    These routes bypass authentication and cause issues when opening links in new tabs.
                    Uncomment temporarily if needed for local testing without auth.
                <Route path="/*" element={<AppLayout />}>
                  <Route path="accounts/create" element={<EditAccountPage />} />
                  <Route path="accounts/:id/edit" element={<EditAccountPage />} />
                  <Route path="accounts/:accountId/users" element={<UsersPage />} />
                  <Route path="accounts/:accountId/users/:id/edit" element={<EditUserPage />} />
                  <Route path="accounts/:accountId/users/create" element={<EditUserPage />} />
                  <Route path="accounts/:accountId/users/:userId/solutions" element={<UserSolutionsPage />} />
                  <Route path="accounts/:accountId/users/:userId/subscriptions" element={<UserSubscriptionsPage />} />
                  <Route path="accounts/:accountId/users/:userId/subscriptions/add" element={<UserSubscriptionsPage />} />
                </Route>
                */}

                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/*" element={<AppLayout />}>
                    <Route
                      path=""
                      element={<DefaultRedirect />}
                    />
                    <Route path="home" element={<HomePage />} />
                    <Route path="profile" element={<UserProfilePage />} />
                    <Route path="accounts" element={<RouteGuard section="accounts"><AccountsPage /></RouteGuard>} />
                    <Route
                      path="accounts/:id/edit"
                      element={<RouteGuard section="accounts"><EditAccountPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/create"
                      element={<RouteGuard section="accounts"><EditAccountPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/inactivity-center"
                      element={<RouteGuard section="accounts"><InactivityCenterPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/:accountId/users"
                      element={<RouteGuard section="accounts"><UsersPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/:accountId/users/:id/edit"
                      element={<RouteGuard section="accounts"><EditUserPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/:accountId/users/create"
                      element={<RouteGuard section="accounts"><EditUserPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/:accountId/users/:userId/solutions"
                      element={<RouteGuard section="accounts"><UserSolutionsPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/:accountId/users/:userId/subscriptions"
                      element={<RouteGuard section="accounts"><UserSubscriptionsPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/:accountId/users/:userId/subscriptions/add"
                      element={<RouteGuard section="accounts"><UserSubscriptionsPage /></RouteGuard>}
                    />
                    <Route
                      path="accounts/:accountId/api-keys"
                      element={<RouteGuard section="accounts"><ApiKeysPage /></RouteGuard>}
                    />
                    <Route
                      path="hxc-partners/:mode"
                      element={<RouteGuard section="hxc-partners"><HXCPartnerCreationPage /></RouteGuard>}
                    />
                    <Route
                      path="hxc-partners"
                      element={<RouteGuard section="hxc-partners"><Navigate replace to="/hxc-partners/staging" /></RouteGuard>}
                    />
                    {sections.map((section) => (
                      <Route
                        key={section.key}
                        path={section.key}
                        element={
                          <DataManagementRouteGuard sectionKey={section.key}>
                            <SectionErrorBoundary>
                              <Suspense fallback={<LoadingFallback />}>
                                <section.PageComponent />
                              </Suspense>
                            </SectionErrorBoundary>
                          </DataManagementRouteGuard>
                        }
                      />
                    ))}
                    <Route path="dashboards" element={<RouteGuard section="dashboards"><DashboardsPage /></RouteGuard>} />
                    <Route path="participants" element={<RouteGuard section="participants"><ParticipantsPage /></RouteGuard>} />
                    <Route path="alerts" element={<RouteGuard section="alerts"><AlertsPage /></RouteGuard>} />
                    <Route path="region-sync" element={<RegionSyncPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Route>
              </Routes>
            </AuthProvider>
          </Auth0ProviderWithNavigate>
        </Router>
      </LoadingContext.Provider>
    </MuiThemeProvider>
  );
}

/**
 * Wrap the App with our ThemeProvider
 */
const AppWithTheme = () => (
  <ThemeProvider>
    <App />
  </ThemeProvider>
);

export default AppWithTheme;
